import { Flex, Heading, Button, Text, Box } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { useSignInModal } from '../SigninHostModal'
import { useEffect } from 'react'
import { StageTitle } from './StageTitle'
import { WalletImage } from './WalletImage'
import { mipdStore } from '@/services/wallet-service'
import { WalletId } from 'thirdweb/wallets'

export enum OperationType {
  WalletRequest,
  SocialRequest,
  SocialSigning,
  WalletSigning,
  Request,
}

export const StatusView = ({
  error,
  isSuccess,
  retryCallback,
  backCallback,
  provider,
  operationType = OperationType.Request,
}: {
  error: boolean
  isSuccess: boolean
  retryCallback?: () => void
  backCallback?: () => void
  provider: string | null
  operationType?: OperationType
}) => {
  const { t } = useTranslation()
  const { onClose } = useSignInModal()
  const isProcessing = !isSuccess && !error

  useEffect(() => {
    if (isSuccess) {
      onClose()
    }
  }, [isSuccess])

  const { title, subtitle } = getMessage({ isError: error, provider, operationType, t })

  return (
    <>
      <StageTitle title={t('messages.connect')} backCallback={error ? backCallback : undefined} />
      <Flex
        mb={{ base: 12, lg: operationType === OperationType.WalletRequest ? 0 : 8 }}
        pt={{ base: 10, lg: '44px' }}
        h={'full'}
        minH={'270px'}
        flexDir={'column'}
        alignItems={'center'}
      >
        <Flex
          display={'flex'}
          pos={'relative'}
          mb={4}
          w={152}
          h={152}
          rounded={40}
          bg={'ninja.500'}
          transition={'0.2s'}
          justifyContent={'center'}
          alignItems={'center'}
          boxShadow={error ? shadows.error : isSuccess ? shadows.success : 'none'}
          __css={{
            '@keyframes drawBorder': {
              '100%': {
                strokeDashoffset: 0,
              },
            },
            rect: {
              animation: '2s linear 0s infinite normal none running drawBorder',
            },
          }}
        >
          <WalletImage width={64} height={64} id={provider} />
          <Box transition={'0.2s'} opacity={isProcessing ? 1 : 0} pos={'absolute'} w={'full'} h={'full'}>
            <svg width="100%" height="100%" viewBox="0 0 152 152">
              <rect
                x="4"
                y="4"
                width="144"
                height="144"
                rx="36"
                stroke="url(#gradient)"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray="120 394"
                strokeDashoffset="514"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#805dff" stopOpacity="1" />
                  <stop offset="100%" stopColor="#805dff" stopOpacity="1" />
                </linearGradient>{' '}
              </defs>
            </svg>
          </Box>
        </Flex>
        <Heading mb={1} textAlign={'center'} fontSize={{ base: '24px', sm: '26px' }}>
          {title}
        </Heading>
        <Text
          textAlign={'center'}
          color={'ninja.300'}
          fontWeight={'700'}
          // h={{ base: '42px', sm: '48px' }}
          fontSize={{ base: 'sm', sm: 'md' }}
        >
          {subtitle}
        </Text>
        {operationType === OperationType.WalletRequest && (
          <Text
            fontSize={'sm'}
            fontWeight={700}
            mt={2}
            px={2}
            py={1}
            bg={'ninja.500'}
            color={'ninja.100'}
            rounded={'lg'}
          >
            {t('signin.network-ethereum')}
          </Text>
        )}
      </Flex>
      <Flex height={'60px'} flexDirection={'column'} justifyContent={'end'}>
        {error && (
          <Button onClick={() => retryCallback?.()} mb={0} w={'full'} h={'56px'} rounded={'xl'}>
            {t('common.try-again')}
          </Button>
        )}
        {/* <Button w={'full'} h={'56px'} rounded={'xl'} variant={'secondary'}>
          Don't have Metamask?
        </Button> */}
      </Flex>
    </>
  )
}

const getMessage = ({
  isError,
  provider,
  operationType,
  t,
}: {
  isError: boolean
  provider: string | null
  operationType: OperationType
  t: (key: string, opts?: Record<string, any>) => string
}) => {
  const isSocial = provider?.startsWith('social')
  const isEmail = provider === 'email'

  if (isEmail || operationType === OperationType.SocialSigning) {
    if (isError) {
      return {
        title: t('signin.connection-failed'),
        subtitle: t('signin.error-try-again'),
      }
    }

    return {
      title: t('signin.please-wait'),
      subtitle: t('signin.connecting-account'),
    }
  }

  if (isSocial) {
    if (isError) {
      return {
        title: t('signin.connection-failed'),
        subtitle: t('signin.error-try-again'),
      }
    }

    const providerName = `${provider?.split('-').at(1)?.at(0)?.toUpperCase()}${provider?.split('-').at(1)?.slice(1)}`

    return {
      title: t('signin.log-in-with', { provider: providerName }),
      subtitle: t('signin.connect-in-provider-window'),
    }
  }

  if (isError) {
    return {
      title: t('signin.connection-failed'),
      subtitle: t('signin.error-authorization'),
    }
  }

  const providerInfo = mipdStore.findProvider({ rdns: provider as WalletId })

  if (providerInfo) {
    return {
      title: t('signin.awaiting-confirmation'),
      subtitle:
        operationType === OperationType.WalletRequest
          ? t('signin.accept-connection', { provider: toCapitalize(providerInfo?.info.name || '') })
          : t('signin.confirm-transaction', { provider: toCapitalize(providerInfo?.info.name || '') }),
    }
  }

  return {
    title: t('signin.awaiting-confirmation'),
    subtitle: t('signin.confirm-transaction-simple'),
  }
}

const toCapitalize = (string: string) => {
  return string.at(0)?.toUpperCase() + string.slice(1)
}

const shadows = {
  error: '0px 4px 48px 0px #FF1B1B inset;',
  success: '0px 4px 48px 0px #4EFF60 inset;',
}
